/**
 * Gauge semáforo del burn rate (gastos / ingresos del periodo).
 *
 * El objetivo del negocio es mantenerse en 70-80%. Ambos extremos
 * son peligrosos: gastar muy poco (< 55%) es sub-uso severo del capital
 * (probablemente no estamos invirtiendo en crecimiento) y gastar
 * demasiado (> 90%) es sobre-uso (poco margen para imprevistos).
 *
 * Zonas:
 *   < 55%    sub-uso severo — rojo
 *   55-70%   bajo el objetivo — naranja
 *   70-80%   EN OBJETIVO — verde
 *   80-90%   sobre el objetivo — naranja
 *   > 90%    alerta — rojo
 */
import { useState } from "react";
import { formatMxnCents } from "@/lib/format";

export type BurnRateTone = "danger-low" | "below" | "target" | "above" | "danger-high";

export function burnRateTone(pct: number): BurnRateTone {
  if (pct < 55) return "danger-low";
  if (pct < 70) return "below";
  if (pct <= 80) return "target";
  if (pct < 90) return "above";
  return "danger-high";
}

/**
 * Tono semáforo para el % de utilidad sobre ingresos. Equivale a aplicar
 * `burnRateTone(100 - pct)` y mapear a success/warning/error: la utilidad
 * y el burn rate son las dos caras de la misma moneda — verde 20-30%
 * utilidad = verde 70-80% burn (en objetivo); negativo = error.
 */
export function utilidadPctTone(pct: number): "success" | "warning" | "error" {
  const burn = 100 - pct;
  if (burn < 55 || burn > 90) return "error";
  if (burn < 70 || burn > 80) return "warning";
  return "success";
}

const TONE_META: Record<BurnRateTone, { color: string; text: string; label: string }> = {
  "danger-low": { color: "oklch(0.65 0.22 25)", text: "text-error", label: "Sub-uso severo" },
  below: { color: "oklch(0.78 0.18 60)", text: "text-warning", label: "Bajo el objetivo" },
  target: { color: "oklch(0.7 0.2 145)", text: "text-success", label: "En objetivo" },
  above: { color: "oklch(0.78 0.18 60)", text: "text-warning", label: "Sobre el objetivo" },
  "danger-high": { color: "oklch(0.65 0.22 25)", text: "text-error", label: "Alerta" },
};

export function BurnRateGauge({
  ingresosCents,
  gastosCents,
  isrCents,
  ivaSaldoCents,
  showBreakdown = true,
  size = "md",
}: {
  ingresosCents: number;
  gastosCents: number;
  /** Si vienen, muestra también "Utilidad neta" debajo de la bruta. */
  isrCents?: number;
  ivaSaldoCents?: number;
  showBreakdown?: boolean;
  size?: "sm" | "md";
}) {
  const hasFiscal = isrCents !== undefined && ivaSaldoCents !== undefined;
  const [mode, setMode] = useState<"bruto" | "neto">("bruto");
  const margin = ingresosCents - gastosCents;
  // En modo neto el "gastado" efectivo incluye ISR + IVA saldo (saldo positivo
  // resta caja, negativo suma porque no descarga). Las zonas del semáforo
  // (55/70/80/90) se mantienen iguales — la lectura cambia: en bruto 80% es
  // el techo del objetivo; en neto un % alto refleja la carga fiscal total.
  const effectiveGastos =
    mode === "neto" && hasFiscal
      ? gastosCents + (isrCents ?? 0) + (ivaSaldoCents ?? 0)
      : gastosCents;
  const pct = ingresosCents > 0 ? (effectiveGastos / ingresosCents) * 100 : 0;
  const capped = Math.min(100, Math.max(0, pct));
  const tone = burnRateTone(pct);
  const c = TONE_META[tone];
  const inTarget = pct >= 70 && pct <= 80;

  // Gauge SVG: pct → angle = 180° - pct·1.8 (0% izq, 100% der).
  const W = size === "sm" ? 220 : 280;
  const H = size === "sm" ? 130 : 160;
  const cx = W / 2;
  const cy = H - (size === "sm" ? 14 : 18);
  const r = size === "sm" ? 88 : 110;
  const strokeWidth = size === "sm" ? 14 : 18;
  const angleOf = (p: number) => 180 - p * 1.8;
  const polar = (degMath: number) => {
    const rad = (degMath * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };
  const arcPath = (fromPct: number, toPct: number) => {
    const p1 = polar(angleOf(fromPct));
    const p2 = polar(angleOf(toPct));
    return `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  };
  const needle = polar(angleOf(capped));

  return (
    <div className="space-y-3">
      {hasFiscal && (
        <label className="flex items-center justify-center gap-3 text-sm select-none">
          <span
            className={mode === "bruto" ? "font-semibold" : "text-base-content/55"}
            title="Burn rate operativo = gastos / ingresos"
          >
            Bruto
          </span>
          <input
            type="checkbox"
            className="toggle toggle-sm"
            checked={mode === "neto"}
            onChange={(e) => setMode(e.target.checked ? "neto" : "bruto")}
            aria-label="Cambiar entre cálculo bruto y neto"
          />
          <span
            className={mode === "neto" ? "font-semibold" : "text-base-content/55"}
            title="Burn rate total = (gastos + ISR + IVA) / ingresos"
          >
            Neto
          </span>
        </label>
      )}
      <div className="flex items-center justify-center relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[300px]" aria-hidden>
          {/* < 55: rojo (sub-uso severo) */}
          <path
            d={arcPath(0, 55)}
            stroke="oklch(0.65 0.22 25)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* 55-70: naranja (bajo el objetivo) */}
          <path
            d={arcPath(55, 70)}
            stroke="oklch(0.78 0.18 60)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* 70-80: verde (en objetivo) */}
          <path
            d={arcPath(70, 80)}
            stroke="oklch(0.7 0.2 145)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* 80-90: naranja (sobre el objetivo) */}
          <path
            d={arcPath(80, 90)}
            stroke="oklch(0.78 0.18 60)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* > 90: rojo (alerta) */}
          <path
            d={arcPath(90, 100)}
            stroke="oklch(0.65 0.22 25)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {[55, 70, 80, 90].map((m) => {
            const ang = angleOf(m);
            const inner = polar(ang);
            const outerRad = r + 8;
            const labelRad = r + 20;
            const rad = (ang * Math.PI) / 180;
            const outer = { x: cx + outerRad * Math.cos(rad), y: cy - outerRad * Math.sin(rad) };
            const label = { x: cx + labelRad * Math.cos(rad), y: cy - labelRad * Math.sin(rad) };
            return (
              <g key={m}>
                <line
                  x1={inner.x}
                  y1={inner.y}
                  x2={outer.x}
                  y2={outer.y}
                  stroke="oklch(0.4 0 0 / 0.4)"
                  strokeWidth={1}
                />
                <text
                  x={label.x}
                  y={label.y + 3}
                  textAnchor="middle"
                  fontSize={9}
                  fill="oklch(0.5 0 0 / 0.7)"
                >
                  {m}
                </text>
              </g>
            );
          })}
          <line
            x1={cx}
            y1={cy}
            x2={needle.x}
            y2={needle.y}
            stroke={c.color}
            strokeWidth={3}
            strokeLinecap="round"
          />
          <circle cx={cx} cy={cy} r={5} fill={c.color} />
        </svg>
        <span
          className={`absolute top-1 right-1 badge badge-xs ${
            tone === "target"
              ? "badge-success"
              : tone === "below" || tone === "above"
                ? "badge-warning"
                : "badge-error"
          }`}
          aria-hidden
        >
          {c.label}
        </span>
      </div>
      <div className="text-center">
        <div
          className={`font-display font-bold ${size === "sm" ? "text-2xl" : "text-4xl"} tabular-nums ${c.text}`}
        >
          {pct.toFixed(1)}%
        </div>
        <div className="text-xs text-base-content/55 mt-0.5">
          {inTarget
            ? "Dentro del objetivo 70-80%"
            : pct < 55
              ? `${(70 - pct).toFixed(1)} pts bajo el objetivo · sub-uso severo`
              : pct < 70
                ? `${(70 - pct).toFixed(1)} pts bajo el objetivo`
                : pct < 90
                  ? `${(pct - 80).toFixed(1)} pts sobre el objetivo`
                  : `${(pct - 80).toFixed(1)} pts sobre el objetivo · alerta`}
        </div>
      </div>
      {showBreakdown && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-base-300/40">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-base-content/55 font-semibold">
              Gastado
            </div>
            <div className="font-display font-bold text-base tabular-nums text-error">
              {formatMxnCents(gastosCents)}
            </div>
            <div className="text-[11px] text-base-content/45">
              de {formatMxnCents(ingresosCents)}
            </div>
          </div>
          {(() => {
            // Saldo positivo (a pagar) resta caja; negativo (a favor) suma
            // porque no descarga. Misma convención que la calculadora.
            const neta = hasFiscal ? margin - (isrCents ?? 0) - (ivaSaldoCents ?? 0) : margin;
            const utilValue = mode === "neto" && hasFiscal ? neta : margin;
            const utilLabel = mode === "neto" && hasFiscal ? "Utilidad neta" : "Utilidad bruta";
            return (
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-base-content/55 font-semibold">
                  {utilLabel}
                </div>
                <div
                  className={`font-display font-bold text-base tabular-nums ${utilValue >= 0 ? "text-success" : "text-error"}`}
                >
                  {formatMxnCents(utilValue)}
                </div>
                <div className="text-[11px] text-base-content/45">
                  {ingresosCents > 0
                    ? `${((utilValue / ingresosCents) * 100).toFixed(1)}% de ingresos`
                    : "—"}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
